import React, { useState, useEffect } from 'react';
import {
  X,
  CreditCard,
  Link as LinkIcon,
  Copy,
  Check,
  AlertCircle,
  Loader2,
  ExternalLink,
  Mail,
  Building2
} from 'lucide-react';
import { invoiceService } from '../../services/api';
import lawpayService from '../../services/lawpayService';
import useModal from '../../hooks/useModal';

const CollectPaymentModal = ({ invoice, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentLink, setPaymentLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [configStatus, setConfigStatus] = useState(null);
  const [checkingConfig, setCheckingConfig] = useState(true);

  const [formData, setFormData] = useState({
    amount: '',
    account_type: 'operating',
    description: `Payment for Invoice ${invoice.invoice_number}`,
    client_email: invoice.client_email || '',
    return_url: window.location.href,
    cancel_url: window.location.href
  });

  const balanceDue = invoiceService.calculateBalance(invoice);
  const maxAmount = Math.max(0, balanceDue);

  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  useEffect(() => {
    checkLawPayConfig();
    // Set default amount to balance due
    setFormData(prev => ({
      ...prev,
      amount: maxAmount.toFixed(2)
    }));
  }, [maxAmount]);

  const checkLawPayConfig = async () => {
    try {
      const response = await lawpayService.getStatus();
      setConfigStatus(response.data);
    } catch (err) {
      console.error('Error checking LawPay config:', err);
      setError('Failed to check LawPay configuration');
    } finally {
      setCheckingConfig(false);
    }
  };

  const handleAmountChange = (value) => {
    const sanitized = value.replace(/[^0-9.]/g, '');
    const parts = sanitized.split('.');
    if (parts.length > 2) return;
    if (parts[1] && parts[1].length > 2) return;
    setFormData(prev => ({ ...prev, amount: sanitized }));
  };

  const setFullAmount = () => {
    setFormData(prev => ({ ...prev, amount: maxAmount.toFixed(2) }));
  };

  const handleGenerateLink = async (e) => {
    e.preventDefault();
    setError('');
    setPaymentLink(null);

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    if (amount > maxAmount) {
      setError(`Payment amount cannot exceed balance due of ${invoiceService.formatCurrency(maxAmount)}`);
      return;
    }

    try {
      setLoading(true);
      const response = await lawpayService.createPaymentLink({
        amount: amount,
        account_type: formData.account_type,
        invoice_id: invoice.id,
        client_email: formData.client_email || null,
        client_name: invoice.client_name,
        description: formData.description,
        expires_in_days: 30 // Links expire after 30 days
      });
      // Map the new response format
      setPaymentLink({
        payment_page_url: response.data.payment_url,
        expires_at: response.data.expires_at
      });
    } catch (err) {
      console.error('Error generating payment link:', err);
      setError(err.response?.data?.detail || 'Failed to generate payment link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!paymentLink?.payment_page_url) return;

    try {
      await navigator.clipboard.writeText(paymentLink.payment_page_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleEmailLink = () => {
    if (!paymentLink?.payment_page_url) return;

    const subject = encodeURIComponent(`Payment Link for Invoice ${invoice.invoice_number}`);
    const body = encodeURIComponent(
      `Dear ${invoice.client_name},\n\n` +
      `Please use the link below to pay Invoice ${invoice.invoice_number}:\n\n` +
      `${paymentLink.payment_page_url}\n\n` +
      `Amount Due: ${invoiceService.formatCurrency(formData.amount)}\n\n` +
      `Thank you for your business.\n\n` +
      `Best regards`
    );

    window.open(`mailto:${formData.client_email}?subject=${subject}&body=${body}`, '_blank');
  };

  if (checkingConfig) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
        onMouseDown={handleBackdropMouseDown}
        onMouseUp={handleBackdropMouseUp}
      >
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8">
          <div className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
            <span className="ml-2 text-gray-600">Checking LawPay configuration...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!configStatus?.configured) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
        onMouseDown={handleBackdropMouseDown}
        onMouseUp={handleBackdropMouseUp}
      >
        <div
          className="bg-white rounded-lg shadow-xl max-w-md w-full"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <CreditCard className="w-6 h-6 text-yellow-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Collect Payment</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="p-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-yellow-800">LawPay Not Configured</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    To collect payments online, you need to configure your LawPay integration in Settings &gt; Payment Integration.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <CreditCard className="w-6 h-6 text-yellow-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Collect Payment via LawPay</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Invoice Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Invoice:</span>
              <span className="font-medium">{invoice.invoice_number}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Client:</span>
              <span className="font-medium">{invoice.client_name}</span>
            </div>
            <div className="flex justify-between items-center border-t pt-2">
              <span className="text-sm font-medium text-gray-900">Balance Due:</span>
              <span className="font-bold text-red-600">{invoiceService.formatCurrency(balanceDue)}</span>
            </div>
          </div>

          {/* Environment Badge */}
          <div className="mb-4">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              configStatus.environment === 'production'
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {configStatus.environment === 'production' ? 'Live Payments' : 'Sandbox Mode'}
            </span>
          </div>

          {!paymentLink ? (
            <form onSubmit={handleGenerateLink} className="space-y-4">
              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Amount *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">$</span>
                  </div>
                  <input
                    type="text"
                    value={formData.amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    className="block w-full pl-8 pr-16 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                    placeholder="0.00"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={setFullAmount}
                      className="text-xs text-yellow-600 hover:text-yellow-700 font-medium"
                      disabled={loading}
                    >
                      Full Amount
                    </button>
                  </div>
                </div>
              </div>

              {/* Account Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Deposit To
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="account_type"
                      value="operating"
                      checked={formData.account_type === 'operating'}
                      onChange={(e) => setFormData(prev => ({ ...prev, account_type: e.target.value }))}
                      className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Operating Account</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="account_type"
                      value="trust"
                      checked={formData.account_type === 'trust'}
                      onChange={(e) => setFormData(prev => ({ ...prev, account_type: e.target.value }))}
                      className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Trust Account</span>
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {formData.account_type === 'trust'
                    ? 'For retainers and client funds held in trust'
                    : 'For earned fees and general payments'}
                </p>
              </div>

              {/* Client Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Email (optional)
                </label>
                <input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="client@example.com"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Used to pre-fill the payment form and for receipts
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center text-red-800">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    <span className="text-sm">{error}</span>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.amount}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-md text-sm font-medium hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <LinkIcon className="w-4 h-4 mr-2" />
                      Generate Payment Link
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            /* Payment Link Generated */
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center text-green-800 mb-2">
                  <Check className="w-5 h-5 mr-2" />
                  <span className="font-medium">Payment Link Generated!</span>
                </div>
                <p className="text-sm text-green-700">
                  Share this link with your client to collect payment of {invoiceService.formatCurrency(formData.amount)}.
                </p>
              </div>

              {/* Payment Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={paymentLink.payment_page_url}
                    readOnly
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                  />
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className={`px-3 py-2 rounded-md text-sm font-medium flex items-center ${
                      copied
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <a
                  href={paymentLink.payment_page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-yellow-500 text-white rounded-md text-sm font-medium hover:bg-yellow-600"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Payment Page
                </a>

                {formData.client_email && (
                  <button
                    type="button"
                    onClick={handleEmailLink}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email to Client
                  </button>
                )}
              </div>

              {paymentLink.expires_at && (
                <p className="text-xs text-gray-500">
                  This link expires on {new Date(paymentLink.expires_at).toLocaleString()}
                </p>
              )}

              {/* Close Button */}
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollectPaymentModal;
